import {ShieldCheck} from "lucide-react";
import {useState} from "react";

import {cn} from "@/lib/utils";
import {useStoredCompany} from "@/src/modules/company/store";
import {Loader} from "@/src/modules/core/components";
import {getAssigneeDisplayName} from "@/src/modules/company-on-guard/lib/assignee-utils";
import {companyOnGuardService} from "@/src/modules/company-on-guard/services";

import type {CompanyOnGuardAssignee} from "@/src/modules/company-on-guard/types";

import {useDispatchOnGuardOfficers} from "../hooks/use-dispatch-on-guard-officers";
import {DISPATCH_DESK_PANEL_CLASS} from "../lib/dispatch-desk-layout";
import {DispatchOnGuardCallInstructionsModal} from "./dispatch-on-guard-call-instructions-modal";
import {
  DispatchDeskCardShell,
  DispatchDeskEmptyMessage,
  DispatchDeskEmptyPanelBody,
  DispatchDeskNestedList,
  DispatchDeskScrollRegion,
  DISPATCH_DESK_CONTENT_MUTED_CLASS,
  DISPATCH_DESK_ROW_PRIMARY_CLASS,
} from "./dispatch-desk-card";

const DISPATCH_ON_GUARD_INSTRUCTIONS_TRIGGER_CLASS = cn(
  "text-primary shrink-0 text-sm leading-snug hover:underline",
  "focus-visible:ring-ring focus-visible:ring-offset-background rounded-sm",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
);

type DispatchOnGuardOfficersProps = {
  className?: string;
};

function OnGuardOfficerRow({
  assignee,
  companyTimezone,
}: {
  assignee: CompanyOnGuardAssignee;
  companyTimezone?: string | null;
}) {
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const name = getAssigneeDisplayName(assignee.assignee);

  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className={cn(DISPATCH_DESK_ROW_PRIMARY_CLASS, "min-w-0 flex-1 break-words")}>
          {name}
        </span>
        <button
          type="button"
          className={DISPATCH_ON_GUARD_INSTRUCTIONS_TRIGGER_CLASS}
          onClick={() => setInstructionsOpen(true)}
        >
          Call instructions
        </button>
      </div>
      <DispatchOnGuardCallInstructionsModal
        open={instructionsOpen}
        onOpenChange={setInstructionsOpen}
        assignee={assignee}
        companyTimezone={companyTimezone}
      />
    </>
  );
}

function OnGuardHeaderSuffix({count}: {count: number}) {
  return (
    <span className={cn("shrink-0", DISPATCH_DESK_CONTENT_MUTED_CLASS)}>
      · {count} on call now
    </span>
  );
}

export function DispatchOnGuardOfficers({className}: DispatchOnGuardOfficersProps) {
  const company = useStoredCompany();
  const canViewPanel = companyOnGuardService.getCanViewSupervisorOnCallPanel();
  const canList = companyOnGuardService.getCanListRoster();
  const {assignees, isPending} = useDispatchOnGuardOfficers();

  if (!canViewPanel) {
    return null;
  }
  return (
    <DispatchDeskCardShell
      className={cn(DISPATCH_DESK_PANEL_CLASS, className)}
      icon={ShieldCheck}
      iconClassName="text-primary"
      title="Supervisor on Call"
      titleSuffix={
        canList && !isPending && assignees.length > 0 ? (
          <OnGuardHeaderSuffix count={assignees.length} />
        ) : null
      }
    >
      <DispatchDeskScrollRegion>
        {!canList ? (
          <DispatchDeskEmptyPanelBody>
            <DispatchDeskEmptyMessage>
              You do not have permission to view supervisors on call.
            </DispatchDeskEmptyMessage>
          </DispatchDeskEmptyPanelBody>
        ) : isPending ? (
          <DispatchDeskEmptyPanelBody>
            <Loader className="py-6" size="sm" />
          </DispatchDeskEmptyPanelBody>
        ) : assignees.length === 0 ? (
          <DispatchDeskEmptyPanelBody>
            <DispatchDeskEmptyMessage>No supervisor on call.</DispatchDeskEmptyMessage>
          </DispatchDeskEmptyPanelBody>
        ) : (
          <DispatchDeskNestedList>
            {assignees.map((assignee) => (
              <OnGuardOfficerRow
                key={assignee.company_on_guard_user_id}
                assignee={assignee}
                companyTimezone={company?.timezone}
              />
            ))}
          </DispatchDeskNestedList>
        )}
      </DispatchDeskScrollRegion>
    </DispatchDeskCardShell>
  );
}
